/* eslint-disable @typescript-eslint/no-explicit-any */
import { type Marker, MarkerClusterer } from '@googlemaps/markerclusterer'
import { InfoWindow, useMap } from '@vis.gl/react-google-maps'
import { Navigation, User } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '@/modules/shared/components/ui/button'

import type { NearbyCustomer } from '../../types/nearby-customers'
import { CustomerMarker } from './marker' // Se existir, importe o componente para marcar clientes

export type ClusteredCustomerMarkersProps = {
  customers: NearbyCustomer[]
}

/**
 * The ClusteredCustomerMarkers component is responsible for integrating the
 * markers with the markerclusterer for customers.
 */
export const ClusteredCustomerMarkers = ({
  customers,
}: ClusteredCustomerMarkersProps) => {
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({})
  const [selectedCustomerKey, setSelectedCustomerKey] = useState<string | null>(
    null,
  )

  const selectedCustomer = useMemo(
    () =>
      customers && selectedCustomerKey
        ? customers.find((c) => c.identidade.toString() === selectedCustomerKey)
        : null,
    [customers, selectedCustomerKey],
  )

  const map = useMap()
  const clusterer = useMemo(() => {
    if (!map) return null

    return new MarkerClusterer({ map })
  }, [map])

  useEffect(() => {
    if (!clusterer) return

    clusterer.clearMarkers()
    clusterer.addMarkers(Object.values(markers))
  }, [clusterer, markers])

  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key])) return markers

      if (marker) {
        return {
          ...markers,
          [key]: marker,
        }
      }
      const { [key]: _, ...newMarkers } = markers
      return newMarkers
    })
  }, [])

  const handleInfoWindowClose = useCallback(() => {
    setSelectedCustomerKey(null)
  }, [])

  const handleMarkerClick = useCallback((customer: NearbyCustomer) => {
    setSelectedCustomerKey(customer.identidade.toString())
  }, [])

  const handleClick = useCallback((customer: NearbyCustomer) => {
    // Redireciona para o aplicativo de navegação com a posição do cliente
    const { latitude, longitude } = customer
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      typeof (window as any).MSStream === 'undefined'

    const url = isIOS
      ? `http://maps.apple.com/?daddr=${latitude},${longitude}`
      : `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches

    if (isDesktop) {
      window.open(url, '_blank')
    } else {
      window.open(url, '_self')
    }
  }, [])

  return (
    <>
      {customers.map((customer) => (
        <CustomerMarker
          key={customer.identidade}
          customer={customer}
          onClick={handleMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}

      {selectedCustomerKey && (
        <InfoWindow
          anchor={markers[selectedCustomerKey]}
          onCloseClick={handleInfoWindowClose}
          className="flex flex-col max-w-[95%] md:max-w-full"
        >
          <p className="text-sm font-medium">{selectedCustomer?.cliente}</p>

          <p className="text-xs text-gray-700 text-wrap">
            {`${selectedCustomer?.endereco}, ${selectedCustomer?.bairro} - ${selectedCustomer?.cidade} - ${selectedCustomer?.uf}`}
          </p>
          <p className="text-xs text-gray-700">{selectedCustomer?.cnpjcpf}</p>

          <p className="text-xs text-gray-700">
            Distancia: {selectedCustomer?.distanciatexto}
          </p>

          <p className="text-xs text-gray-700">
            Duração: {selectedCustomer?.duracaotexto}
          </p>

          <div className="flex flex-1 mt-6 gap-2 mb-4">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => handleClick(selectedCustomer)}
            >
              <Navigation size={16} className="mr-2" />
              Abrir no GPS
            </Button>
            <Link to={`/cliente/view/${selectedCustomer?.identidade}`}>
              <Button size="sm" variant="outline" className="flex-1">
                <User size={16} className="mr-2" />
                Perfil
              </Button>
            </Link>
          </div>
        </InfoWindow>
      )}
    </>
  )
}
